
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SolarPanelElectricalConnectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Solar Panel Electrical Connection',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional solar panel electrical wiring and grid connection in Fresno, CA. Complete electrical integration for residential and commercial solar systems.',
    url: `${siteUrl}/services/renewables-ev/solar-panel-electrical-connections`,
  };

  return (
    <>
      <SEOHead
        title="Solar Panel Electrical Connection Fresno CA | Solar Wiring"
        description="Professional solar panel electrical wiring and grid connection in Fresno, CA. Complete electrical integration for solar systems. Federal tax credits available."
        canonical="/services/renewables-ev/solar-panel-electrical-connections"
        keywords="solar panel wiring Fresno, solar electrical connection, solar panel installation, grid connection"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Solar Panel Electrical Connections — Fresno, CA"
        heroSubtitle="Expert electrical integration for solar energy systems"
        heroImage="https://public.readdy.ai/ai/img_res/45fd3a2a92e77f999cf4d05d529f2bd9.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20solar%20panel%20electrical%20junction%20box%20and%20DC%20disconnect%20switch%20mounted%20on%20exterior%20wall%20with%20conduit%20and%20wiring%20connections%20showing%20professional%20electrical%20work%20for%20solar%20system%20California%20residential%20installation%20no%20people&width=700&height=600&seq=solar-wiring-fresno-001&orientation=portrait"
        fresnoContent="Fresno's exceptional solar potential with 270+ sunny days annually makes solar panel installation highly profitable, but proper electrical integration is critical for safety and performance. Our licensed electricians specialize in solar electrical work, ensuring your system connects safely to your home's electrical panel and PG&E's grid. We handle all electrical aspects of solar installations throughout Fresno County including Clovis, Sanger, and surrounding Central Valley communities. With California's 30% federal solar tax credit and PG&E's net metering program, proper electrical installation maximizes your solar investment and energy savings."
        features={[
          {
            icon: 'ri-flashlight-line',
            title: 'DC Wiring Installation',
            description: 'Safe routing of DC power from solar panels to inverter',
          },
          {
            icon: 'ri-plug-2-line',
            title: 'AC Interconnection',
            description: 'Professional connection from inverter to electrical panel',
          },
          {
            icon: 'ri-shut-down-line',
            title: 'Rapid Shutdown Systems',
            description: 'Code-required safety disconnects for emergency situations',
          },
          {
            icon: 'ri-exchange-line',
            title: 'Grid Interconnection',
            description: "PG&E net metering connection and approval",
          },
          {
            icon: 'ri-dashboard-3-line',
            title: 'Production Monitoring',
            description: 'Electrical setup for solar monitoring systems',
          },
          {
            icon: 'ri-shield-check-line',
            title: 'Code Compliant',
            description: 'All work meets NEC Article 690 and California Title 24',
          },
        ]}
        whatsIncluded={[
          'Complete electrical system design and engineering',
          'City of Fresno electrical permits and plan review',
          'DC wiring from solar panels to inverter location',
          'Conduit installation and proper cable management',
          'AC disconnect and rapid shutdown system installation',
          'Main electrical panel interconnection',
          'Dedicated solar breaker installation',
          'Grounding and bonding per NEC requirements',
          'PG&E interconnection application and approval',
          'Net metering setup and utility coordination',
          'Production monitoring system wiring',
          'Complete system testing and commissioning',
          'City of Fresno and PG&E final inspections',
          'Federal solar tax credit documentation (30%)',
          '10-year electrical workmanship warranty',
        ]}
        faqs={[
          {
            question:
              'What electrical work is required for solar panel installation in Fresno?',
            answer:
              'Solar panel electrical work includes several critical components: DC wiring from panels to inverter (typically in conduit on your roof and exterior walls), AC wiring from inverter to your main electrical panel, installation of required disconnects and rapid shutdown systems, main panel interconnection with dedicated solar breaker, grounding and bonding, and PG&E meter upgrade for net metering. All work must meet National Electrical Code Article 690 and California Title 24 standards. We handle every electrical aspect of your solar installation, coordinating with your solar installer for seamless integration.',
          },
          {
            question: "Can my Fresno home's electrical panel handle solar panels?",
            answer:
              'Most modern Fresno homes with 200-amp electrical panels can accommodate solar systems up to 10kW without panel upgrades. However, the "120% rule" limits total breaker capacity, so homes with older 100-amp panels or heavily loaded 200-amp panels may need upgrades. We perform a detailed load calculation and panel assessment during our free consultation. If upgrades are needed, we handle everything including panel replacement, permits, and inspections. Panel upgrades cost $1,500-$3,000 but are often necessary for optimal solar system performance and are included in the 30% federal solar tax credit.',
          },
          {
            question: 'How does PG&E net metering work with solar panels in Fresno?',
            answer:
              "PG&E's net metering (NEM 3.0) allows your solar system to send excess electricity to the grid in exchange for credits. During sunny days, your solar panels produce more power than you use, and the excess flows to PG&E's grid. At night or on cloudy days, you draw power from the grid using your accumulated credits. We handle all PG&E interconnection paperwork, including the net metering application and utility coordination. PG&E typically installs a bidirectional meter within 2-4 weeks of system completion. Net metering can reduce your annual electricity costs by 70-90% in Fresno.",
          },
          {
            question: 'What permits are required for solar electrical work in Fresno?',
            answer:
              'Solar electrical installations in Fresno require an electrical permit from the City of Fresno Building Department and approval from PG&E for grid interconnection. The permit process includes submitting electrical plans, load calculations, and equipment specifications. We handle all permit applications, plan reviews, and inspections as part of our service. Fresno typically processes residential solar permits within 2-3 weeks. After installation, both City of Fresno and PG&E inspections are required before your system can be energized. All our work meets NEC and California Title 24 requirements.',
          },
          {
            question: 'How long does solar panel electrical installation take in Fresno?',
            answer:
              'The electrical portion of solar installation typically takes 1-2 days for residential systems. This includes running conduit, DC and AC wiring, installing disconnects, connecting to your electrical panel, and initial testing. The timeline depends on system size, roof complexity, and distance from panels to inverter and electrical panel. Permit processing adds 2-3 weeks, and PG&E interconnection approval takes another 2-4 weeks. Total timeline from permit application to system activation is typically 6-8 weeks. We coordinate closely with your solar installer to ensure efficient project completion.',
          },
          {
            question: 'Is solar power worth it in Fresno, California?',
            answer:
              "Absolutely! Fresno is one of California's best locations for solar energy with 270+ sunny days annually and high solar irradiance. With PG&E's electricity rates averaging $0.35-$0.45 per kWh and rising, most Fresno homeowners see 20-30% reductions in annual electricity costs with solar. The federal solar tax credit covers 30% of total system costs (including electrical work), and systems typically pay for themselves in 6-8 years. Over a 25-year system lifespan, Fresno homeowners save $30,000-$60,000 on electricity costs while increasing home value and reducing carbon footprint.",
          },
        ]}
        ctaText="Get Your Free Solar Electrical Quote"
      />
    </>
  );
}
